import React, { useEffect, useState, useRef } from "react";
import GridViewIcon from "@mui/icons-material/GridView";
import ReorderIcon from "@mui/icons-material/Reorder";
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Grid,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from "@mui/material";
import ArchiveIcon from "@mui/icons-material/Archive";
import StarIcon from "@mui/icons-material/Star";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { useDispatch, useSelector } from "react-redux";
import {
  getEvents,
  selectEvents,
  resetEvents,
  updateEventStatus,
  deleteEvent,
  deleteEventsWithoutTags,
  annotateEvents,
  removeAnnotations,
  setEvents,
} from "./eventsSlice";
import { selectFilters, setFilterApplied } from "../filters/filterSlice";
import { Filters } from "../filters/Filters";
import { selectOrganization } from "../organization/organizationSlice";
import WeatherCard from "./WeatherCard";
import WeatherGrid from "./WeatherGrid";

export function EventsTable() {
  const [state, setState] = useState({ page: 0, rowsPerPage: 10 });
  const [selected, setSelected] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [listView, setListView] = useState(true);
  const [reload, setReload] = useState(false);
  const [tab, setTab] = useState(0);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAnnotateMenu, setShowAnnotateMenu] = useState(false);
  const [selectedAnnotations, setSelectedAnnotations] = useState([]);
  const [deleteOption, setDeleteOption] = useState("all"); // 'all' or 'withoutTags'
  const { species: allSpecies } = useSelector(selectOrganization);
  const prevTab = useRef(tab);
  const isFirstRender = useRef(true);
  const justRan = useRef(false);
  const { results: events, count } = useSelector(selectEvents);
  const filters = useSelector(selectFilters);
  const dispatch = useDispatch();
  const isFirstRun = useRef(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Get current user from localStorage
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isSuperUser = user?.username === "lumsadmin" || user?.is_staff === true;

  // Function to check if events have tags (fire/smoke)
  const getEventsWithTags = (eventIds) => {
    const selectedEvents = events.filter((event) =>
      eventIds.includes(event.uuid),
    );
    return selectedEvents.filter(
      (event) =>
        event.species &&
        event.species.some((s) => s.id === "fire" || s.id === "smoke"),
    );
  };

  const eventsWithTags = getEventsWithTags(selected);
  const eventsCountWithTags = eventsWithTags.length;
  const eventCountWithoutTags = selected.length - eventsCountWithTags;

  const status = (tab) => {
    switch (tab) {
      case 0:
        return "";
      case 1:
        return "ARCHIVED";
      case 2:
        return "FEATURED";
      default:
        return "NONE";
    }
  };

  const { page, rowsPerPage } = state;

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    setSelected([]);
    dispatch(resetEvents());
    showChanges(true);
  }, [rowsPerPage]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (justRan.current) {
      justRan.current = false;
      return;
    }
    dispatch(
      getEvents(
        state.page + 1,
        filters.filterApplied,
        status(tab),
        rowsPerPage,
      ),
    );
    justRan.current = true;
    let check = filters.filterApplied ? false : filters.filterApplied;
    dispatch(setFilterApplied(check));
  }, [filters]);

  useEffect(() => {
    if (prevTab !== tab && reload == true) {
      prevTab.current = tab;
      reloadEvents();
    } else {
      setReload(true);
    }
  }, [tab]);

  useEffect(() => {
    if (selectMode == false) {
      setSelected([]);
    }
  }, [selectMode]);

  const showChanges = async (tabChange = false) => {
    if (!tabChange) {
      if (events.length <= rowsPerPage) {
        dispatch(resetEvents());
      } else {
        dispatch(
          setEvents({
            results: events.slice(0, rowsPerPage * page),
            count: count,
            filterApplied: true,
          }),
        );
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    dispatch(
      getEvents(
        state.page + 1,
        filters.filterApplied,
        status(tab),
        rowsPerPage,
      ),
    );
  };

  const reloadEvents = (pageSize = 10) => {
    justRan.current = false;
    setState({ page: 0, rowsPerPage: pageSize });

    setSelected([]);
    dispatch(resetEvents());
    showChanges(true);
  };

  const handleChangePage = (event, newPage) => {
    if (newPage > state.page && newPage + 1 > events.length / rowsPerPage)
      dispatch(
        getEvents(newPage + 1, filters.filterApplied, status(tab), rowsPerPage),
      );
    setState({ page: newPage, rowsPerPage: state.rowsPerPage });
    setSelected([]);
  };

  const handleChangeRowsPerPage = (event) => {
    setState({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };

  const handleArchive = () => {
    if (tab !== 1) {
      dispatch(updateEventStatus(selected, "archive"));
    } else {
      dispatch(updateEventStatus(selected, "restore"));
    }
    setSelected([]);
    if (tab == 2 || tab == 1) {
      showChanges();
    }
  };

  const handleStar = () => {
    if (tab !== 2) {
      dispatch(updateEventStatus(selected, "feature"));
    } else {
      dispatch(updateEventStatus(selected, "restore"));
    }
    setSelected([]);
    if (tab == 2 || tab == 1) {
      showChanges();
    }
  };

  const handleDelete = () => {
    if (deleteOption === "all") {
      dispatch(deleteEvent(selected));
    } else {
      dispatch(deleteEventsWithoutTags(selected));
    }
    setShowDeleteConfirmation(false);
    setSelected([]);
    setDeleteOption("all");
    showChanges();
  };

  const handleAnnotate = () => {
    dispatch(annotateEvents(selected, selectedAnnotations));
    setShowAnnotateMenu(false);
    setSelected([]);
    showChanges();
  };

  const handleUnAnnotate = () => {
    dispatch(removeAnnotations(selected, selectedAnnotations));
    setShowAnnotateMenu(false);
    setSelected([]);
    showChanges();
  };

  const changeUrl = (img) => {
    return img;
  };

  const changeName = (name) => {
    let nameC;
    if (name.includes("PTZ")) {
      nameC = name.split("-");
      name = nameC[1];
    }
    return name;
  };

  const gradientStyle = {
    background:
      "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
    color: "white",
  };

  // Check if any selected events have tags for the delete without tags option
  const hasEventsWithTags = eventsCountWithTags > 0;

  return (
    <Paper className="mb4" sx={{ p: 0, m: 0, overflow: "hidden" }}>
      {/* Delete Options Dialog */}
      <Dialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        aria-labelledby="delete-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{ bgcolor: "#f44336", color: "white" }}
        >
          Delete Events
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText sx={{ mb: 2 }}>
            You have selected <strong>{selected.length}</strong> event(s) to
            delete.
            {eventsCountWithTags > 0 && (
              <span
                style={{ color: "#f44336", display: "block", marginTop: "8px" }}
              >
                ⚠️ {eventsCountWithTags} of these events contain Fire/Smoke
                tags.
              </span>
            )}
          </DialogContentText>

          {isSuperUser ? (
            <FormControl component="fieldset" sx={{ mt: 2, width: "100%" }}>
              <FormLabel component="legend">Select deletion option:</FormLabel>
              <RadioGroup
                value={deleteOption}
                onChange={(e) => setDeleteOption(e.target.value)}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        🗑️ Delete all selected events
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        This will delete <strong>{selected.length}</strong>{" "}
                        event(s) including those with Fire/Smoke tags.
                        {eventsCountWithTags > 0 && (
                          <span style={{ color: "#f44336" }}>
                            {" "}
                            ({eventsCountWithTags} with tags will be permanently
                            deleted)
                          </span>
                        )}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="withoutTags"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        🔒 Delete events without tags only
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        This will delete{" "}
                        <strong>{eventCountWithoutTags}</strong> event(s) that
                        have no Fire/Smoke tags. Events with Fire/Smoke tags
                        will be preserved.
                        {eventsCountWithTags > 0 && (
                          <span style={{ color: "#4caf50" }}>
                            {" "}
                            ({eventsCountWithTags} events with tags will be
                            kept)
                          </span>
                        )}
                      </Typography>
                    </Box>
                  }
                  disabled={eventCountWithoutTags === 0}
                />
              </RadioGroup>
            </FormControl>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}
            >
              ℹ️ You can only delete events without Fire/Smoke tags to preserve
              historical data. Events with tags will be automatically preserved.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Button
            onClick={() => setShowDeleteConfirmation(false)}
            sx={{ color: "#666" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            sx={{
              bgcolor: deleteOption === "all" ? "#f44336" : "#4caf50",
              "&:hover": {
                bgcolor: deleteOption === "all" ? "#d32f2f" : "#388e3c",
              },
            }}
            disabled={!isSuperUser && eventCountWithoutTags === 0}
          >
            {deleteOption === "all"
              ? `Delete All ${selected.length} Event(s)`
              : `Delete ${eventCountWithoutTags} Event(s) Without Tags`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Annotate Dialog - same as before */}
      <Dialog
        open={showAnnotateMenu}
        onClose={() => setShowAnnotateMenu(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Annotate Event(s)</DialogTitle>
        <DialogContent dividers>
          <DialogContentText id="alert-dialog-description">
            Select all the labels you want to apply (or remove)!
          </DialogContentText>
          <FormGroup>
            {allSpecies.map((species) => (
              <FormControlLabel
                control={<Checkbox name={species.name} />}
                label={species.name}
                key={species.name}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedAnnotations([
                      ...selectedAnnotations,
                      species.name,
                    ]);
                  } else {
                    setSelectedAnnotations(
                      selectedAnnotations.filter(
                        (item) => item !== species.name,
                      ),
                    );
                  }
                }}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAnnotateMenu(false);
              setSelectedAnnotations([]);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleUnAnnotate}>UnAnnotate</Button>
          <Button onClick={handleAnnotate} autoFocus>
            Annotate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rest of the component remains the same */}
      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 0,
          background:
            "linear-gradient(135deg, #a9a9a9 0%, rgba(40, 39, 39, 0.95) 100%)",
          zIndex: 2,
          px: 0,
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          aria-label="basic tabs example"
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile={isMobile}
          sx={{
            "& .MuiTab-root": {
              fontSize: isMobile ? "0.7rem" : "0.875rem",
              minWidth: isMobile ? "auto" : 90,
              px: isMobile ? 1 : 2,
              color: "white",
            },
          }}
        >
          <Tab label="All" />
          <Tab label="Archived" />
          <Tab label="Featured" />
        </Tabs>

        <Box
          sx={{
            position: "absolute",
            right: 15,
            top: 8,
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 0.5 : 1,
            flexDirection: isMobile ? "row" : "row",
            flexWrap: isMobile ? "wrap" : "nowrap",
            justifyContent: isMobile ? "flex-end" : "flex-start",
          }}
        >
          <Filters />

          <Button
            onClick={() => setSelectMode(!selectMode)}
            variant="text"
            size="small"
            sx={{
              border: "white",
              minWidth: isMobile ? "auto" : "auto",
              px: isMobile ? 1 : 2,
              py: isMobile ? 0.5 : 0.5,
              borderRadius: "30px",
              color: "white",
              fontSize: isMobile ? "0.7rem" : "0.875rem",
              fontWeight: 500,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "white",
                color: "grey",
                transform: "translateY(-2px)",
                transition: "all 0.3s ease",
              },
            }}
          >
            Select
          </Button>

          {listView ? (
            <Tooltip title="Grid View">
              <GridViewIcon
                sx={{
                  color: "white",
                  cursor: "pointer",
                  fontSize: isMobile ? 20 : 24,
                  "&:hover": { opacity: 0.7 },
                }}
                onClick={() => setListView(!listView)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="List view">
              <ReorderIcon
                sx={{
                  color: "white",
                  cursor: "pointer",
                  fontSize: isMobile ? 20 : 24,
                  "&:hover": { opacity: 0.7 },
                }}
                onClick={() => setListView(!listView)}
              />
            </Tooltip>
          )}
        </Box>

        {selected.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: isMobile ? 1 : 2,
              py: isMobile ? 1 : 1,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 1 : 0,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {selected.length} selected
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: isMobile ? 1 : 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Button
                size="small"
                onClick={() => {
                  setShowAnnotateMenu(true);
                  setSelectedAnnotations([]);
                }}
                sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
              >
                Annotations Menu
              </Button>
              <Box sx={{ display: "flex", gap: 1 }}>
                <div onClick={handleArchive} style={{ cursor: "pointer" }}>
                  {tab === 1 ? (
                    <UnarchiveIcon
                      sx={{ color: "red", fontSize: isMobile ? 20 : 24 }}
                    />
                  ) : (
                    <ArchiveIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
                  )}
                </div>
                <StarIcon
                  onClick={handleStar}
                  sx={{
                    color: tab === 2 ? "red" : "black",
                    cursor: "pointer",
                    fontSize: isMobile ? 20 : 24,
                  }}
                />
                <DeleteIcon
                  onClick={() => setShowDeleteConfirmation(true)}
                  sx={{
                    color: "red",
                    cursor: "pointer",
                    fontSize: isMobile ? 20 : 24,
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Table/Grid content remains the same */}
      <Box
        sx={{
          p: isMobile ? (listView ? 0 : 1) : listView ? 2 : 2,
          minHeight: 400,
        }}
      >
        {listView ? (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              border: "1px solid #e0e0e0",
              overflowX: "auto",
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {selectMode && (
                    <TableCell padding="checkbox" sx={{ width: 40 }}>
                      <Checkbox
                        checked={
                          events.length > 0 &&
                          events
                            .slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage,
                            )
                            .every((event) => selected.includes(event.uuid))
                        }
                        onChange={() => {
                          const currentPageEvents = events.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          );
                          const allSelected = currentPageEvents.every((event) =>
                            selected.includes(event.uuid),
                          );
                          if (allSelected) {
                            setSelected(
                              selected.filter(
                                (id) =>
                                  !currentPageEvents.some((e) => e.uuid === id),
                              ),
                            );
                          } else {
                            setSelected([
                              ...selected,
                              ...currentPageEvents
                                .map((e) => e.uuid)
                                .filter((id) => !selected.includes(id)),
                            ]);
                          }
                        }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {events.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={selectMode ? 2 : 1} align="center">
                      <Typography sx={{ py: 4, color: "#999" }}>
                        No events found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (rowsPerPage > 0
                    ? events.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                    : events
                  ).map((row) => (
                    <TableRow key={row.uuid} hover>
                      {selectMode && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selected.includes(row.uuid)}
                            onChange={() => {
                              if (selected.includes(row.uuid)) {
                                setSelected(
                                  selected.filter((id) => id !== row.uuid),
                                );
                              } else {
                                setSelected([...selected, row.uuid]);
                              }
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ p: 0 }}>
                        <WeatherCard data={row} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <div>
            {selectMode ? (
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <Checkbox
                  checked={events
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((event) => event.uuid)
                    .every((item) => selected.includes(item))}
                  onChange={() => {
                    if (selected.length === rowsPerPage) setSelected([]);
                    else
                      setSelected(
                        events
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          .map((event) => event.uuid),
                      );
                  }}
                />
                <span>
                  <h4>Select All</h4>
                </span>
              </span>
            ) : null}
            <Grid
              container
              spacing={isMobile ? 1 : 2}
              style={{ justifyContent: "center" }}
            >
              {events
                .slice(
                  state.page * state.rowsPerPage,
                  state.page * state.rowsPerPage + state.rowsPerPage,
                )
                .map((event) => {
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={2.4}
                      key={event.uuid}
                      sx={{ display: "flex", justifyContent: "center" }}
                    >
                      <div
                        className="card rounded my-3 shadow-lg back-card"
                        style={{
                          width: isMobile ? "calc(100% - 10px)" : "235px",
                          maxWidth: "235px",
                          margin: "5px",
                          height: "fit-content",
                          maxHeight: "460px",
                        }}
                        key={event.uuid}
                      >
                        <WeatherGrid data={event} />
                      </div>
                    </Grid>
                  );
                })}
            </Grid>
          </div>
        )}
      </Box>

      <TablePagination
        rowsPerPageOptions={[10, 20, 50, 100]}
        component="div"
        count={count !== null ? count : "loading..."}
        rowsPerPage={rowsPerPage}
        page={page}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onPageChange={handleChangePage}
        showFirstButton
        sx={{
          "& .MuiTablePagination-toolbar": {
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? 1 : 0,
            minHeight: isMobile ? "auto" : "auto",
            padding: isMobile ? "8px 16px" : "0 16px",
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select":
            {
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            },
        }}
      />
    </Paper>
  );
}
